import {
	AutogrowTextArea,
	AutogrowTextAreaHandle,
	RewriteToolsMenu,
	RewriteToolsResult,
	usePlainTextRewriteTools,
	ValidationError
} from "client/Components";
import { SubtitleElementSaveModel } from "client/Api/Hub/common.composer";
import { IInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import { getClientId, hasClientId } from "client/Misc/clientId";
import { localize } from "client_legacy/ndaw/culture";
import { Guid } from "client_legacy/ndaw/guid";
import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import styles from "./Subtitle.module.less";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { ensureNotNil, generateTrackingId } from "client/Misc/utils";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useIsTouchDevice } from "client/Hooks";
import { InlineActionList } from "../../Actions/InlineActionList";
import { RewriteToolEditorKind, useRewriteToolsService } from "client/Features/ChatAssistant/useRewriteToolsService";
import { hubItemKindToRewriteToolTargetKindMap } from "client/Features/Hub/common";
import { useIsAiAssistantAvailable } from "client/Features/ChatAssistant/useIsAiAssistantAvailable";

export type SubtitleProps = {
	attachable: IValueInputAttachable<SubtitleElementSaveModel>;
	genericHandlers: ElementGenericActionHandlers;
	ref: Ref<ElementGenericHandle>;
	hubItemKind: HubItemKind;
};

export const Subtitle = (props: SubtitleProps): ReactElement => {
	const {
		attachable,
		genericHandlers,
		ref,
		hubItemKind
	} = props;

	const {
		value: elementModel
	} = attachable;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const {
		activate
	} = useBuilderEditor();

	const [isImproveTextMenuOpen, setIsImproveTextMenuOpen] = useState<boolean>(false);
	const [selectionRange, setSelectionRange] = useState<[number, number]>([0, 0]);

	const isAiAssistantAvailable: boolean = useIsAiAssistantAvailable();
	const isTouchDevice: boolean = useIsTouchDevice();

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const textAreaRef: RefObject<AutogrowTextAreaHandle | null> = useRef<AutogrowTextAreaHandle>(null);
	const improveTextButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const textAttachable: IInputAttachable<string> = attachable.for("text");

	const {
		reducer
	} = textAttachable;

	const value: string = reducer.value;

	const changeValueHandler = useCallback(
		(newValue: string): void => {
			reducer.setValue(newValue);
		},
		[reducer]
	);

	const closeImproveTextMenuHandler = useCallback(
		(): void => {
			setIsImproveTextMenuOpen(false);
		},
		[]
	);

	const focusInputHandler = useCallback(
		(): void => {
			ensureNotNil(textAreaRef.current)
				.focus();
		},
		[]
	);

	const improveTextResult: RewriteToolsResult<string, string> = usePlainTextRewriteTools(
		value,
		changeValueHandler,
		focusInputHandler,
		{
			kind: "specific-range",
			from: selectionRange[0],
			to: selectionRange[1],
			shouldUseEntireContentWhenEmpty: true
		},
		useRewriteToolsService(
			hubItemKindToRewriteToolTargetKindMap[hubItemKind],
			RewriteToolEditorKind.PlainText
		)
	);

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(elementModel) && getClientId(elementModel) === clientId) {
					focusInputHandler();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[elementModel, focusInputHandler]
	);

	return (
		<>
			<ElementLayout
				hasError={!attachable.validation.isValid()}
				trackingId={attachable.reducer.path}
				ref={rootRef}
				onCloseAllMenus={closeImproveTextMenuHandler}
			>
				<ElementLayout.ActionsMenu trackingId="element-actions">
					<ElementLayout.AddElementMenuItem
						onAddNewElementToColumn={onAddNewElementToColumn}
						onOpenEmbedInputDialog={onOpenEmbedInputDialog}
						onOpenVideoInputDialog={onOpenVideoInputDialog}
						onSelectImage={onSelectImage}
						trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
					/>
					{onMoveUp && (
						<ElementLayout.MoveUpMenuItem
							onClick={onMoveUp}
						/>
					)}
					{onMoveDown && (
						<ElementLayout.MoveDownMenuItem
							onClick={onMoveDown}
						/>
					)}
					<ElementLayout.DuplicateMenuItem
						onClick={onDuplicate}
					/>
					<ElementLayout.DeleteMenuItem
						onClick={onDelete}
					/>
					{isAiAssistantAvailable
						&& !isTouchDevice
						&& improveTextResult.canRewrite
						&& (
							<>
								<InlineActionList.Divider />
								<ElementLayout.ImproveTextMenuItem
									onClick={() => setIsImproveTextMenuOpen(true)}
									ref={improveTextButtonRef}
								/>
								{isImproveTextMenuOpen && (
									<RewriteToolsMenu
										onClose={closeImproveTextMenuHandler}
										target={ensureNotNil(improveTextButtonRef.current)}
										trackingId="improve-text__drop-over"
										handlers={improveTextResult.handlers}
									/>
								)}
							</>
						)
					}
				</ElementLayout.ActionsMenu>
				<div
					className={styles.root}
				>
					<AutogrowTextArea
						{...textAttachable.attachToTextArea()}
						ref={textAreaRef}
						placeholder={localize("_EnterSubtitle")}
						hasNoPadding
						isBorderless
						onFocus={() => activate()}
						hasNoBackgroundColor
						onSelectionChange={(from, to) => setSelectionRange([from, to])}
					/>
				</div>
				<ValidationError {...attachable.for("text").attachToValidationError()} />
			</ElementLayout>
		</>
	);
};
Subtitle.displayName="Subtitle";

